import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../shared/api';

const ContractOffers = ({ contractId, offers, readOnly = true }) => {
  const { t } = useTranslation();
  const [downloadingOfferId, setDownloadingOfferId] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('cs-CZ');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('cs-CZ');
  };

  const getTemplateBadgeClass = (template) => {
    switch (template) {
      case 'minimal':
        return 'badge bg-info-lt';
      case 'professional':
        return 'badge bg-success-lt';
      default:
        return 'badge bg-primary-lt';
    }
  };

  const handleGeneratePDF = async (offerId) => {
    // Prevent multiple simultaneous downloads
    if (downloadingOfferId) {
      return;
    }

    setDownloadingOfferId(offerId);

    try {
      // Make API request with blob response type and timeout
      const response = await api.get(
        `contracts/api/contract-offers/${offerId}/download_pdf/`,
        {
          responseType: 'blob',
          timeout: 60000, // 60 second timeout for large PDFs
        }
      );

      // Validate response
      if (!response || response.size === 0) {
        throw new Error('Empty PDF response received');
      }

      // Check if response is actually a PDF
      const contentType = response.type;
      if (contentType && !contentType.includes('application/pdf')) {
        // Handle error response (might be JSON error)
        if (contentType.includes('application/json')) {
          const text = await response.text();
          const errorData = JSON.parse(text);
          throw new Error(errorData.error || 'PDF generation failed');
        }
        throw new Error(`Invalid content type: ${contentType}`);
      }

      // Extract filename from Content-Disposition header if available
      let filename = `contract_offer_${offerId}.pdf`;

      // Try to get a better filename from the response headers
      // Note: The api module might not expose headers, but we try anyway
      if (response.headers && response.headers['content-disposition']) {
        const disposition = response.headers['content-disposition'];
        const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Create blob URL
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Create and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();

      // Cleanup with proper timing
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      // Success notification (optional - you can add toast notifications here)

    } catch (err) {
      console.error('Error downloading PDF:', err);

      // Provide detailed error message
      let errorMessage = t('contracts.pdfDownloadError') || 'Failed to download PDF';

      if (err.message) {
        errorMessage += `: ${err.message}`;
      } else if (err.response?.data?.error) {
        errorMessage += `: ${err.response.data.error}`;
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = t('contracts.pdfDownloadTimeout') || 'PDF download timed out. Please try again.';
      } else if (!navigator.onLine) {
        errorMessage = t('common.noInternetConnection') || 'No internet connection';
      }

      alert(errorMessage);
    } finally {
      setDownloadingOfferId(null);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>{t('contracts.tabs.offers')}</h5>
        {readOnly && (
          <span className="badge bg-info-lt">{t('contracts.contractReadOnly')}</span>
        )}
      </div>

      {offers.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-muted">{t('contracts.noOffers')}</p>
        </div>
      ) : (
        <div className="row row-cards">
          {offers.map((offer) => (
            <div key={offer.id} className="col-sm-6 col-lg-4">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h3 className="card-title mb-0">{offer.name}</h3>
                    <div className="dropdown">
                      <button className="btn btn-outline-secondary btn-sm dropdown-toggle" data-bs-toggle="dropdown">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon">
                          <circle cx="12" cy="12" r="1"></circle>
                          <circle cx="12" cy="5" r="1"></circle>
                          <circle cx="12" cy="19" r="1"></circle>
                        </svg>
                      </button>
                      <ul className="dropdown-menu">
                        <li>
                          <button
                            className="dropdown-item"
                            onClick={() => handleGeneratePDF(offer.id)}
                            disabled={downloadingOfferId === offer.id}
                          >
                            {downloadingOfferId === offer.id ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {t('contracts.generatingPDF') || 'Generating...'}
                              </>
                            ) : (
                              <>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon me-2">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                  <polyline points="14,2 14,8 20,8"></polyline>
                                  <line x1="16" y1="13" x2="8" y2="13"></line>
                                  <line x1="16" y1="17" x2="8" y2="17"></line>
                                  <polyline points="10,9 9,9 8,9"></polyline>
                                </svg>
                                {t('contracts.generatePDF')}
                              </>
                            )}
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="mb-2">
                    <span className={getTemplateBadgeClass(offer.template)}>
                      {t(`contracts.templates.${offer.template}`) || offer.template}
                    </span>
                    {offer.discount > 0 && (
                      <span className="badge bg-warning-lt ms-2">{offer.discount}% {t('contracts.fields.discount')}</span>
                    )}
                  </div>

                  <div className="text-muted small mb-2">
                    <div><strong>{t('contracts.customer')}:</strong> {offer.customer_name || '-'}</div>
                    <div><strong>{t('contracts.contractor')}:</strong> {offer.contractor_name || '-'}</div>
                    <div><strong>{t('contracts.fields.version')}:</strong> {offer.version}</div>
                  </div>

                  {offer.description && (
                    <p className="text-muted small mb-2">{offer.description}</p>
                  )}

                  <div className="text-muted small">
                    <div><strong>{t('contracts.fields.dateIssued')}:</strong> {formatDate(offer.date_issued)}</div>
                    <div><strong>{t('contracts.fields.validUntil')}:</strong> {formatDate(offer.offer_valid_until)}</div>
                    <div><strong>{t('common.createdAt')}:</strong> {formatDateTime(offer.created_at)}</div>
                  </div>

                  <div className="mt-2">
                    <div className="form-check form-switch form-check-inline">
                      <input className="form-check-input" type="checkbox" checked={offer.include_items} disabled />
                      <label className="form-check-label small text-muted">{t('contracts.includeItems')}</label>
                    </div>
                    <div className="form-check form-switch form-check-inline">
                      <input className="form-check-input" type="checkbox" checked={!offer.hide_prices} disabled />
                      <label className="form-check-label small text-muted">{t('contracts.showPrices')}</label>
                    </div>
                    <div className="form-check form-switch form-check-inline">
                      <input className="form-check-input" type="checkbox" checked={offer.include_vat} disabled />
                      <label className="form-check-label small text-muted">{t('contracts.includeVAT')}</label>
                    </div>
                  </div>

                  {offer.note && (
                    <div className="mt-2">
                      <small className="text-muted">
                        <strong>{t('contracts.fields.note')}:</strong> {offer.note}
                      </small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContractOffers;
